import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Loader2,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  GraduationCap,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { HRStaffService, type RecruitApplication } from '../services/hr-staff.service';

interface RecruitApplicationDetailProps {
  recruitId: string;
  onError: (error: string) => void;
  onClose: () => void;
}

const FUNNEL_STEP_NAMES = [
  'Invite Viewed', 'Account Created', 'Docs Signed', 'Training Completed',
  'Test Passed', 'Ready to Sell', 'First Sale', 'First Paycheck',
];

const getStepStatus = (currentStep: number, stepNumber: number) => {
  if (stepNumber < currentStep) return 'completed';
  if (stepNumber === currentStep) return 'current';
  return 'pending';
};

const getStepIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'current':
      return <Clock className="w-5 h-5 text-blue-500" />;
    default:
      return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
  }
};

const getDocumentStatusBadge = (status: string) => {
  switch (status) {
    case 'SIGNED':
    case 'COMPLETED':
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Signed</span>;
    case 'PENDING':
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>;
    case 'IN_PROGRESS':
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">In Progress</span>;
    case 'REJECTED':
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Rejected</span>;
    default:
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
  }
};

export default function RecruitApplicationDetail({
  recruitId,
  onError,
  onClose,
}: RecruitApplicationDetailProps) {
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<RecruitApplication | null>(null);

  const loadApplication = useCallback(async () => {
    try {
      setLoading(true);
      const result = await HRStaffService.getRecruitApplication(recruitId);
      setApplication(result);
    } catch (error: any) {
      console.error('Error loading application:', error);
      onError(error.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [recruitId, onError]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recruit Application</h2>
              {application && (
                <p className="text-sm text-gray-500">{application.firstName} {application.lastName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : application ? (
            <>
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{application.email}</span>
                    </div>
                    {application.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{application.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Joined: {new Date(application.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-4">Organization</h3>
                  <div className="space-y-3">
                    {application.organization && (
                      <div className="flex items-center gap-3">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {application.organization.name} ({application.organization.type})
                        </span>
                      </div>
                    )}
                    {application.referrer && (
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Referred by: {application.referrer.firstName} {application.referrer.lastName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Funnel Progress */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-4">Onboarding Progress</h3>
                <div className="flex items-center justify-between">
                  {FUNNEL_STEP_NAMES.map((stepName, index) => {
                    const stepNumber = index + 1;
                    const status = getStepStatus(application.currentFunnelStep, stepNumber);
                    const completedAt = (application.funnelProgress as any)[`step${stepNumber}CompletedAt`];

                    return (
                      <React.Fragment key={stepNumber}>
                        <div className="flex flex-col items-center">
                          {getStepIcon(status)}
                          <span className={`text-xs mt-1 text-center max-w-[80px] ${status === 'completed' ? 'text-green-600' :
                            status === 'current' ? 'text-blue-600 font-medium' :
                              'text-gray-400'
                            }`}>
                            {stepName}
                          </span>
                          {completedAt && (
                            <span className="text-xs text-gray-400 mt-0.5">
                              {new Date(completedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {index < FUNNEL_STEP_NAMES.length - 1 && (
                          <ChevronRight className={`w-4 h-4 ${status === 'completed' ? 'text-green-300' : 'text-gray-300'
                            }`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Training Schedule */}
              {application.trainingSchedule && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">Training Schedule</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <p className="font-medium text-gray-900">{application.trainingSchedule.status}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Preferred Slot</p>
                      <p className="font-medium text-gray-900">
                        {new Date(application.trainingSchedule.preferredSlot1).toLocaleString()}
                      </p>
                    </div>
                    {application.trainingSchedule.confirmedSlot && (
                      <div>
                        <p className="text-xs text-gray-500">Confirmed Slot</p>
                        <p className="font-medium text-gray-900">
                          {new Date(application.trainingSchedule.confirmedSlot).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Test Results */}
              {application.testAttempts.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">Test Results</h3>
                  </div>
                  <div className="space-y-2">
                    {application.testAttempts.map((attempt) => (
                      <div
                        key={attempt.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${attempt.passed ? 'bg-green-50' : 'bg-red-50'
                          }`}
                      >
                        <div>
                          <p className="font-medium text-gray-900">{attempt.testName}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(attempt.attemptedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                            {attempt.score}%
                          </p>
                          <p className={`text-sm ${attempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                            {attempt.passed ? 'Passed' : 'Failed'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {application.documents.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">Documents</h3>
                  </div>
                  <div className="space-y-2">
                    {application.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{doc.name}</p>
                          <p className="text-sm text-gray-500">{doc.type}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getDocumentStatusBadge(doc.status)}
                          {doc.signedDocumentUrl && (
                            <a
                              href={doc.signedDocumentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tax Documents (only shown to ED/C level HR) */}
              {application.taxDocuments.length > 0 && (
                <div className="bg-white border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <h3 className="font-medium text-gray-900">Tax Documents (Restricted Access)</h3>
                  </div>
                  <div className="space-y-2">
                    {application.taxDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{doc.name}</p>
                          <p className="text-sm text-gray-500">{doc.type}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getDocumentStatusBadge(doc.status)}
                          {doc.signedDocumentUrl && (
                            <a
                              href={doc.signedDocumentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              View
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-500 py-12">Failed to load application</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
